import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import { classNames, formattedDate } from "../../../utils";
import LoadingSpinner from "../../../shared-components/LoadingSpinner";
import CheckBox from "../../../shared-components/CheckBox";
import { usePermissionValidator } from "../../../api/UserPermissionValidators";
import { FilterIcon } from "../../../shared-components/icons";
import { Modal } from "../../../shared-components/Modal";
import { ControlGroupProvider } from "../../../api/ControlGroup";
import { PopMenu } from "../../../shared-components/PopMenu";
import Button, { ButtonColorTypes } from "../../../shared-components/Button";
import ElipseIcon from "../../../shared-components/icons/ElipseIcon";
import { RecurArgs, RecurConfigEx } from "../../../api/Person";
import { xpagePageNumberTotalCount } from "../../../api/GenericModels";
import {
  useCancelRecur,
  useRecurEdit,
  useRestartRecur,
  useGetRecurList,
  useGetRecurFilter,
} from "../../../api/PersonApi";
import { useNavigate, useParams } from "react-router-dom";
import {
  CalendarIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import RecurCreateWrapper from "../../../recur/RecurCreateWrapper";
import PaginationInMemory from "../../../shared-components/PaginationInMemory";
import SubscriptionsTableFilter from "./SubscriptionsTableFilter";
import RecurNextRunEditForm from "../../../recur/RecurNextRunEditForm";

export type TableHeader =
  | string
  | {
      text: string;
      customClass?: string;
      hasSort?: boolean;
      sortController?: string;
      withCheckbox?: boolean;
      custom?: "" | "cancel";
    };

type Props = {
  type: "active" | "cancelled";
};

const headerActive: TableHeader[] = [
  {
    text: "NAME / CLIENT REF ID",
    hasSort: true,
    sortController: "name",
    customClass: "flex-1",
    withCheckbox: false,
  },
  "CHECK TYPE / BEHAVIOR KIT",
  "NEXT SCHEDULED RUN",
  "LAST REPORT RUN",
  "FREQUENCY",
  "PAST REPORTS",
];

const headersCancelled: TableHeader[] = [
  {
    text: "NAME / CLIENT REF ID",
    hasSort: true,
    sortController: "name",
    customClass: "flex-1",
    withCheckbox: false,
  },
  "CHECK TYPE / BEHAVIOR KIT",
  "DATE CANCELLED",
  "CANCELLED BY",
  "FREQUENCY",
  "PAST REPORTS",
  "ACTIONS",
];

// Memoized header components to prevent unnecessary re-renders
const TableHeaderComponent = React.memo(
  ({
    header,
    idx,
    headerCheckboxRef,
    headerCheckboxClicked,
    setIsConfirmModalOpen,
    cancelRecur,
    selectedFilterItems,
    setIsFilterModalOpen,
  }: {
    header: TableHeader;
    idx: number;
    headerCheckboxRef: React.RefObject<HTMLInputElement>;
    headerCheckboxClicked: () => void;
    setIsConfirmModalOpen: (open: boolean) => void;
    cancelRecur: any;
    selectedFilterItems: string[];
    setIsFilterModalOpen: (open: boolean) => void;
  }) => (
    <div
      className={classNames(
        "flex p-3 text-xs leading-4 font-medium bg-gray-50 uppercase",
        typeof header === "string" ? "flex-1" : header.customClass ?? ""
      )}
      key={`${idx}-${typeof header === "string" ? header : header.text}`}
    >
      <div className="flex items-center gap-2">
        {typeof header !== "string" && header.withCheckbox && (
          <CheckBox
            checked={false}
            ref={headerCheckboxRef}
            onChange={headerCheckboxClicked}
          />
        )}
        {typeof header !== "string" && header.custom === "cancel" ? (
          <button
            type="button"
            className="px-3 py-1 rounded bg-gray-200 text-gray-800 text-xs font-medium hover:bg-gray-300"
            onClick={() => setIsConfirmModalOpen(true)}
            disabled={cancelRecur.isLoading || cancelRecur.isError}
          >
            Cancel
          </button>
        ) : (
          <>
            {typeof header === "string" ? header : header.text}
            {typeof header === "string"
              ? ""
              : header.hasSort && (
                  <FilterIcon
                    width={16}
                    className={`my-div ${
                      selectedFilterItems?.length > 0 ? "fill-ourGreen" : ""
                    }`}
                    onClick={() => {
                      setIsFilterModalOpen(true);
                    }}
                  />
                )}
          </>
        )}
      </div>
    </div>
  )
);

export const SubscriptionsTable = React.memo((props: Props) => {
  const { companyId, subid } = useParams();
  const activeCompanyId = useMemo(
    () => (!!subid ? subid : companyId ?? ""),
    [subid, companyId]
  );

  const permissions = usePermissionValidator();

  // Memoize headers to prevent unnecessary re-creation
  const initialHeaders = useMemo(
    () => (props.type === "active" ? headerActive : headersCancelled),
    [props.type]
  );

  // Memoize permission-based header modification
  const headersWithPermissions = useMemo(() => {
    if (
      permissions.person.canCancelRecur &&
      typeof initialHeaders[0] !== "string"
    ) {
      const modifiedHeaders = [...initialHeaders];

      if (typeof modifiedHeaders[0] !== "string") {
        modifiedHeaders[0] = {
          ...modifiedHeaders[0],
          withCheckbox: props.type === "active",
        };
      }
      return modifiedHeaders;
    }
    return initialHeaders;
  }, [initialHeaders, permissions.person.canCancelRecur, props.type]);

  const getRecurList = useGetRecurList();
  const cancelRecur = useCancelRecur();
  const restartRecur = useRestartRecur();
  const editRecur = useRecurEdit();

  const [headers, setHeaders] = useState<TableHeader[]>(headersWithPermissions);
  const [toCancel, setToCancel] = useState<string[]>([]);
  const [headerChecked, setHeaderChecked] = useState<boolean>(false);
  const [indeterminate, setIndeterminate] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [size] = useState<number>(20);
  const [total, setTotal] = useState<number>(0);

  const {
    data: recurFilterData,
    isLoading: loadingFilterData,
    mutate: getRecurFilter,
  } = useGetRecurFilter();
  const [recurList, setRecurList] = useState<RecurConfigEx[]>([]);
  const [searchString, setSearchString] = useState<string>("");
  const [selectedFilterItems, setSelectedFilterItems] = useState<string[]>([]);

  // Users without view-all reports only see subscriptions they requested.
  const individual = !permissions.canViewAllReportsWithinCompany;

  // Memoize the reload function to prevent unnecessary re-creation
  const reloadRecurFilter = useCallback(
    (search: string, companyUUID: string, type: "active" | "cancelled") => {
      getRecurFilter({
        company_uuid: companyUUID,
        q: search,
        active: type === "active",
        individual,
      });
    },
    [getRecurFilter, individual]
  );

  // Memoize the reloadRecur function
  const reloadRecur = useCallback(
    (inputPage?: number, recurUUIDs?: string[]) => {
      const params: RecurArgs = {
        company_uuid: activeCompanyId,
        recur_uuids: recurUUIDs
          ? recurUUIDs.join(",")
          : selectedFilterItems.join(","),
        active: props.type === "active",
        size: size,
        page: inputPage ?? page,
        individual,
      };
      getRecurList.mutate(params);
    },
    [
      activeCompanyId,
      getRecurList,
      page,
      props.type,
      size,
      selectedFilterItems,
      individual,
    ]
  );

  useEffect(() => {
    reloadRecurFilter(searchString, activeCompanyId, props.type);
  }, [reloadRecurFilter, searchString, activeCompanyId, props.type]);

  useEffect(() => {
    if (getRecurList.data) {
      setRecurList(getRecurList.data.data);
      setTotal(xpagePageNumberTotalCount(getRecurList.data, size));
    }
  }, [getRecurList.data, size]);

  const headerCheckboxRef = useRef<HTMLInputElement>(null);

  // Memoize the header checkbox click handler
  const headerCheckboxClicked = useCallback(() => {
    if (!recurList || recurList.length === 0) {
      return;
    }
    if (toCancel.length !== recurList.length) {
      setToCancel(recurList.map((a) => a.recur_uuid));
    } else {
      setToCancel([]);
    }
  }, [recurList, toCancel.length]);

  // Memoize the updateToCancel function
  const updateToCancel = useCallback((key: string, checked: boolean) => {
    setToCancel((prev) => {
      const temp: string[] = prev.filter((a) => a !== key);
      if (checked) {
        temp.push(key);
      }
      return temp;
    });
  }, []);

  // Memoize the updatePage function
  const updatePage = useCallback(
    (page: number) => {
      setPage(page);
      reloadRecur(page);
    },
    [reloadRecur]
  );

  // Memoize the header update effect
  useEffect(() => {
    if (props.type !== "active") return;
    if (toCancel.length === 0) {
      setHeaders([...headersWithPermissions]);
    } else {
      setHeaders([
        {
          text: "cancel",
          custom: "cancel",
          customClass: "flex-1",
          withCheckbox: true,
        },
        ...headersWithPermissions.slice(1),
      ]);
    }
    if (headerCheckboxRef.current) {
      if (!recurList || recurList.length === 0) {
        return;
      }
      let partial = false;
      if (toCancel.length === 0) {
        setHeaderChecked(false);
      } else if (toCancel.length === recurList.length) {
        setHeaderChecked(true);
      } else {
        partial = true;
      }
      setIndeterminate(partial);
    }
  }, [toCancel, props.type, recurList, headersWithPermissions]);

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.checked = headerChecked;
      headerCheckboxRef.current.indeterminate = indeterminate;
    }
  }, [headerCheckboxRef, headerChecked, indeterminate]);

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState<boolean>(false);

  // Memoize the cancel implementation
  const handleCancelImpl = useCallback(async (): Promise<void> => {
    await cancelRecur.mutateAsync({ recur_uuids: toCancel.join(",") });
    setToCancel([]);
    reloadRecur();
  }, [cancelRecur, toCancel, reloadRecur]);

  const [isRestartModalOpen, setIsRestartModalOpen] = useState<boolean>(false);
  const [restartUuids, setRestartUuids] = useState<string[]>(["", ""]);

  // Memoize the restart handler
  const handleRestart = useCallback(
    (recur_uuid: string, request_uuid: string): void => {
      setRestartUuids([recur_uuid, request_uuid]);
      setIsRestartModalOpen(true);
    },
    []
  );

  const [isFilterModalOpen, setIsFilterModalOpen] = useState<boolean>(false);

  const handleCloseFilter = useCallback((): void => {
    setIsFilterModalOpen(false);
  }, []);

  useEffect(() => {
    reloadRecur();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.type, activeCompanyId, getRecurList.mutate]);

  // Memoize the save filter handler
  const handleSaveFilter = useCallback(
    (recurUUIDs: string[], search: string) => {
      setPage(1);
      reloadRecur(1, recurUUIDs);
      setIsFilterModalOpen(false);
      setSelectedFilterItems(recurUUIDs);
      setSearchString(search);
    },
    [reloadRecur]
  );

  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [editUuid, setEditUuid] = useState<string>("");
  const [editStartDate, setEditStartDate] = useState<string>(
    new Date().toISOString()
  );
  function handleEditOpen(recur_uuid: string): void {
    setEditUuid(recur_uuid);
    setIsEditModalOpen(true);
  }
  async function handleEditSave(): Promise<void> {
    const now = new Date();
    let convertedDate = new Date(editStartDate);
    convertedDate.setHours(convertedDate.getHours() + now.getUTCHours());
    await editRecur.mutateAsync({
      recur_uuid: editUuid,
      next_run_date: convertedDate.toISOString(),
    });
    setEditUuid("");
    setIsEditModalOpen(false);
    reloadRecur();
  }

  // Memoize the loading state
  const isLoading = useMemo(
    () =>
      getRecurList.isLoading || cancelRecur.isLoading || restartRecur.isLoading,
    [getRecurList.isLoading, cancelRecur.isLoading, restartRecur.isLoading]
  );

  // Memoize the error state
  const hasError = useMemo(
    () => getRecurList.isError || cancelRecur.isError || restartRecur.isError,
    [getRecurList.isError, cancelRecur.isError, restartRecur.isError]
  );

  // Memoize the empty state
  const isEmpty = useMemo(() => recurList.length === 0, [recurList.length]);

  return (
    <ControlGroupProvider>
      <div>
        <div className="flex text-gray-500 rounded-t-xl border shadow shadow-gray-300">
          {headers.map((header, idx) => (
            <TableHeaderComponent
              key={`header-${idx}`}
              header={header}
              idx={idx}
              headerCheckboxRef={headerCheckboxRef}
              headerCheckboxClicked={headerCheckboxClicked}
              setIsConfirmModalOpen={setIsConfirmModalOpen}
              cancelRecur={cancelRecur}
              selectedFilterItems={selectedFilterItems}
              setIsFilterModalOpen={setIsFilterModalOpen}
            />
          ))}
        </div>
        <div className="bg-white border-l border-r  rounded-b-xl shadow shadow-gray-300">
          {isLoading ? (
            <LoadingSpinner />
          ) : hasError ? (
            <div className="flex items-center justify-center h-full p-4">
              <ExclamationTriangleIcon className="w-8 h-8 text-gray-500 mr-2" />
              <div className="text-gray-500">Error loading subscriptions</div>
            </div>
          ) : isEmpty ? (
            <div>No search Items</div>
          ) : (
            recurList.map((recur, i) => (
              <SubscriptionsRow
                recurRow={recur}
                key={`${i}-${recur.recur_uuid}`}
                type={props.type}
                canCancelSubscriptions={permissions.person.canCancelRecur}
                canCreateSubscriptions={permissions.person.canCreateRecur}
                canEditSubscriptions={permissions.person.canEditRecur}
                checked={toCancel.includes(recur.recur_uuid)}
                update={updateToCancel}
                handleRestart={handleRestart}
                handleEdit={handleEditOpen}
              />
            ))
          )}
        </div>
        <PaginationInMemory
          pageSize={size}
          currentPage={page}
          totalCount={total}
          pageChange={updatePage}
        />
      </div>
      {isFilterModalOpen && (
        <Modal
          open={isFilterModalOpen}
          width="w-[600px]"
          onClose={handleCloseFilter}
        >
          <SubscriptionsTableFilter
            type={props.type}
            companyUUID={activeCompanyId}
            onSave={handleSaveFilter}
            onCancel={handleCloseFilter}
            filterList={recurFilterData ?? []}
            search={searchString}
            selected={selectedFilterItems}
            loading={loadingFilterData}
            onSearch={(search) =>
              reloadRecurFilter(search, activeCompanyId, props.type)
            }
          />
        </Modal>
      )}

      {isConfirmModalOpen && (
        <Modal
          open={isConfirmModalOpen}
          width="w-[600px]"
          onClose={() => setIsConfirmModalOpen(false)}
        >
          <div className="flex">
            <div className="bg-red-100 text-red-600 rounded-full p-2 w-12 h-12">
              <ExclamationTriangleIcon className="red w-8 h-8" />
            </div>
            <div>
              <div className="text-xl p-2 font-semibold">Cancel Report(s)?</div>
              <div className="text-md p-2 text-gray-500">
                Are you sure you want to cancel the selected reports? This
                action cannot be undone.
              </div>
            </div>
          </div>
          <div className="flex flex-row-reverse gap-4">
            <Button
              text="Yes, Cancel"
              onClick={() => {
                setIsConfirmModalOpen(false);
                handleCancelImpl();
              }}
              colorType={ButtonColorTypes.secondary}
            />
            <Button
              text="No"
              onClick={() => {
                setIsConfirmModalOpen(false);
              }}
              colorType={ButtonColorTypes.cancel}
            />
          </div>
        </Modal>
      )}
      {isRestartModalOpen && (
        <RecurCreateWrapper
          recur_uuid={restartUuids[0]}
          report_uuid={restartUuids[1]}
          isOpen={isRestartModalOpen}
          setModalOpen={setIsRestartModalOpen}
          onSuccess={reloadRecur}
        />
      )}
      {isEditModalOpen && (
        <RecurNextRunEditForm
          setOpen={setIsEditModalOpen}
          startDate={editStartDate}
          setStartDate={setEditStartDate}
          save={handleEditSave}
        />
      )}
    </ControlGroupProvider>
  );
});

interface RecurRowProps {
  recurRow: RecurConfigEx;
  type: "active" | "cancelled";
  canCancelSubscriptions: boolean;
  canCreateSubscriptions: boolean;
  canEditSubscriptions: boolean;
  checked: boolean;
  update: (key: string, checked: boolean) => void;
  handleRestart: (recur_uuid: string, request_uuid: string) => void;
  handleEdit: (recur_uuid: string) => void;
}

// Memoize the SubscriptionsRow component to prevent unnecessary re-renders
const SubscriptionsRow = React.memo(
  ({
    recurRow,
    type,
    canCancelSubscriptions,
    canCreateSubscriptions,
    canEditSubscriptions,
    checked,
    update,
    handleRestart,
    handleEdit,
  }: RecurRowProps) => {
    const navigate = useNavigate();

    // Memoize the navigation handler
    const handleNavigate = useCallback(() => {
      navigate(recurRow.recur_uuid);
    }, [navigate, recurRow.recur_uuid]);

    // Memoize the checkbox change handler
    const handleCheckboxChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        update(recurRow.recur_uuid, e.target.checked);
      },
      [update, recurRow.recur_uuid]
    );

    // Memoize the restart handler
    const handleRestartClick = useCallback(() => {
      handleRestart(recurRow.recur_uuid, recurRow.last_run_uuid ?? "");
    }, [handleRestart, recurRow.recur_uuid, recurRow.last_run_uuid]);

    // Memoize the check types display
    const checkTypesDisplay = useMemo(() => {
      if (recurRow.check_types.length > 0) {
        return (
          <>
            <div>{recurRow.check_types[0].blueprint_name}</div>
            <div className="text-xs text-gray-500">
              {recurRow.check_types[0].behavior_kit_name ?? "General"}
            </div>
          </>
        );
      }
      return <div>Not Set</div>;
    }, [recurRow.check_types]);

    // Memoize the schedule display
    const scheduleDisplay = useMemo(
      () => (
        <>
          {recurRow.schedule_value}
          {recurRow.schedule_type === "interval" ? " months" : ""}
        </>
      ),
      [recurRow.schedule_value, recurRow.schedule_type]
    );

    // Memoize the menu items
    const menuItems = useMemo(
      () =>
        canCreateSubscriptions && recurRow.last_run_uuid
          ? [
              {
                component: <div>Restart Subscription</div>,
                onClick: handleRestartClick,
              },
            ]
          : [],
      [canCreateSubscriptions, recurRow.last_run_uuid, handleRestartClick]
    );

    return (
      <div className="flex py-2">
        <SubscriptionsRowCell>
          <div className="flex items-center gap-2">
            {type === "active" && canCancelSubscriptions && (
              <CheckBox checked={checked} onChange={handleCheckboxChange} />
            )}
            <div className="flex flex-col">
              <div>{recurRow.full_name}</div>
              <div className="text-xs text-gray-500">{recurRow.custom_id}</div>
            </div>
          </div>
        </SubscriptionsRowCell>
        <SubscriptionsRowCell>
          <div className="flex flex-col">{checkTypesDisplay}</div>
        </SubscriptionsRowCell>
        {type === "active" && (
          <>
            <SubscriptionsRowCell>
              {canEditSubscriptions && recurRow.next_run_date && (
                <CalendarIcon
                  className="w-5"
                  onClick={() => handleEdit(recurRow.recur_uuid)}
                />
              )}
              {recurRow.next_run_date
                ? formattedDate(recurRow.next_run_date)
                : "Calculating"}
            </SubscriptionsRowCell>
            <SubscriptionsRowCell>
              {formattedDate(recurRow.last_run_date)}
            </SubscriptionsRowCell>
          </>
        )}
        {type === "cancelled" && (
          <>
            <SubscriptionsRowCell>
              {formattedDate(recurRow.modified_at)}
            </SubscriptionsRowCell>
            <SubscriptionsRowCell>
              {recurRow.modified_by_name}
            </SubscriptionsRowCell>
          </>
        )}
        <SubscriptionsRowCell>{scheduleDisplay}</SubscriptionsRowCell>
        <SubscriptionsRowCell>
          <span
            className="text-sm text-blue-500 cursor-pointer"
            onClick={handleNavigate}
          >
            View Reports
          </span>
        </SubscriptionsRowCell>
        {type === "cancelled" && (
          <SubscriptionsRowCell noTruncate={true}>
            <PopMenu button={<ElipseIcon />} menuItems={menuItems} />
          </SubscriptionsRowCell>
        )}
      </div>
    );
  }
);

// Memoize the SubscriptionsRowCell component
const SubscriptionsRowCell = React.memo(
  ({
    children,
    noTruncate,
  }: {
    children: React.ReactNode;
    noTruncate?: boolean;
  }) => {
    return (
      <div
        className={classNames(
          "flex px-3 gap-2 items-center flex-1 w-4 text-gray-600 text-sm",
          noTruncate ? "" : "truncate"
        )}
      >
        {children}
      </div>
    );
  }
);
